/**
 * A2P (Application-to-Person) SMS Verification Types
 * For Twilio 10DLC Registration (Sole Proprietor or Low Volume Standard)
 */

// Registration Type for A2P
export type A2PRegistrationType = 'sole_proprietor' | 'low_volume_standard';

export interface A2PVerificationData {
  // Step 0: Registration Type Selection
  registrationType?: A2PRegistrationType;

  // Step 1: EIN Information (only for low_volume_standard)
  hasEIN: boolean;
  ein?: string;

  // Sole Proprietor: Personal Information (SSN-based)
  ssn?: string; // Social Security Number (only for sole_proprietor)

  // Step 2: Business Information (for Low Volume Standard)
  // OR Personal Information (for Sole Proprietor)
  businessName: string; // For Sole Proprietor: This is their full legal name
  businessType: string;
  businessEmail: string;
  businessPhone: string;
  website?: string;

  // Authorized Representative (the person signing up - their real name)
  authorizedRepFirstName: string;
  authorizedRepLastName: string;

  // Business Address (for Low Volume Standard)
  // OR Home Address (for Sole Proprietor)
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;

  // Step 3: Campaign Information
  campaignName: string;
  campaignDescription: string;
  useCase: string;
  sampleMessages: { message: string }[];
  monthlyVolume: number;
}

export interface A2PVerificationStatus {
  id: string;
  userId: string;
  agencyId?: string;
  status: 'not_submitted' | 'pending_payment' | 'pending' | 'verified' | 'rejected' | 'expired';

  // Registration type
  brandType?: A2PRegistrationType;

  // Carrier + Telnyx sole-prop PIN step
  provider?: 'twilio' | 'telnyx';
  otpPending?: boolean;

  // Twilio IDs
  twilioBrandId?: string;
  twilioCampaignId?: string;
  messagingServiceSid?: string;

  // Status tracking
  brandStatus: 'pending' | 'approved' | 'rejected';
  campaignStatus: 'pending' | 'approved' | 'rejected';

  // Timestamps
  submittedAt?: string;
  brandApprovedAt?: string;
  campaignApprovedAt?: string;
  verifiedAt?: string;
  rejectedAt?: string;
  expiresAt?: string;

  // Rejection info
  rejectionReason?: string;
  retryCount: number;
  previousRejectionReason?: string;

  // Payment info
  paymentRequired: boolean;
  paymentId?: string;
  paymentStatus?: 'pending' | 'paid' | 'failed';

  createdAt: string;
  updatedAt: string;
}

export interface PendingPhoneNumber {
  id: string;
  phoneNumber: string;
  phoneSid: string;
  status: 'pending_campaign' | 'added_to_campaign' | 'failed';
  campaignStatus?: string;
  campaignAddedAt?: string;
  purchasedAt: string;
}

export interface A2PPayment {
  id: string;
  userId: string;
  verificationId?: string;
  amount: number;
  currency: string;
  stripePaymentIntentId?: string;
  status: 'pending' | 'paid' | 'failed' | 'refunded';
  paidAt?: string;
  createdAt: string;
}

export interface A2PCheckoutSession {
  sessionId: string;
  sessionUrl: string;
  amount: number;
  currency: string;
}

// Steps: 0=Type Selection, 1=EIN, 1.5=CP575 Upload (Low Volume only), 2=Business, 3=Campaign, 4=Payment
// Using numeric steps with 1.5 represented as step index mapping
export type WizardStep = 0 | 1 | 2 | 3 | 4 | 5; // 0=Type, 1=EIN, 2=CP575(LV), 3=Business, 4=Campaign, 5=Payment

export interface WizardState {
  currentStep: WizardStep;
  completedSteps: WizardStep[];
  data: Partial<A2PVerificationData>;
  errors: Record<string, string>;
}

// Business type options
export const BUSINESS_TYPES = [
  'Sole Proprietorship',
  'LLC',
  'Corporation',
  'Partnership',
  'Non-Profit',
  'Other',
] as const;

export type BusinessType = typeof BUSINESS_TYPES[number];

// Use case options for campaigns - Insurance industry focused
export const USE_CASES = [
  'Customer Care', // Primary: Client service and support
  'Account Notifications', // Policy status updates and confirmations
  'Customer Reminders', // Appointment reminders and scheduled follow-ups
  'Low Volume Mixed', // General insurance communications
  '2FA', // Security verification for client portals
  'Mixed', // Combination of transactional and informational
] as const;

export type UseCase = typeof USE_CASES[number];

// Insurance-specific use case details for A2P registration
export const USE_CASE_DETAILS: Record<UseCase, {
  description: string;
  examples: string[];
  recommended: boolean;
}> = {
  'Customer Care': {
    description: 'Client service communications, quote follow-ups, and support responses',
    examples: [
      'Hi {name}, this is {agent} from {agency}. I received your quote request and wanted to follow up. Would you have time for a quick call today?',
      'Thank you for choosing {agency} for your insurance needs. Reply to this message if you have any questions about your coverage.',
    ],
    recommended: true,
  },
  'Account Notifications': {
    description: 'Policy status updates, application confirmations, and important account changes',
    examples: [
      'Hi {name}, your life insurance policy #{policy} has been successfully activated. Your coverage is now in effect.',
      'Hi {name}, your life insurance application has been received. We will contact you shortly with next steps.',
    ],
    recommended: true,
  },
  'Customer Reminders': {
    description: 'Appointment reminders and scheduled follow-ups',
    examples: [
      'Reminder: You have an appointment with {agent} tomorrow at {time}. Please call us if you need to reschedule. Reply STOP to opt out.',
      'Hi {name}, just following up on our conversation about your life insurance options. When would be a good time to connect?',
    ],
    recommended: true,
  },
  'Low Volume Mixed': {
    description: 'General insurance business communications with mixed message types',
    examples: [
      'Hi {name}, this is {agent} from {agency}. I have some great news about life insurance options that may fit your needs. Call me when you get a chance!',
      'Thank you for your interest in final expense insurance. I\'ll be reaching out shortly to discuss your options.',
    ],
    recommended: true,
  },
  '2FA': {
    description: 'Two-factor authentication codes for secure client portal access',
    examples: [
      'Your verification code for {agency} Client Portal is: {code}. This code expires in 10 minutes.',
      'Security alert: A login attempt was made to your {agency} account. If this wasn\'t you, contact us immediately.',
    ],
    recommended: false,
  },
  'Mixed': {
    description: 'Combination of service messages, reminders, and general updates',
    examples: [
      'Hi {name}, just checking in to see if you had any questions about the life insurance quote we discussed.',
      'Good news! We found a policy that could save you money on your coverage. Give us a call to learn more.',
    ],
    recommended: true,
  },
};

// Recommended use case for insurance agents
export const RECOMMENDED_USE_CASE: UseCase = 'Low Volume Mixed';

// Sample messages template for insurance agents
// Uses {AGENT_NAME} and {BUSINESS_NAME} placeholders — replaced at runtime with actual agent data
// IMPORTANT: At least 3-4 templates should include opt-out language for TCR compliance
export const SAMPLE_MESSAGE_TEMPLATES = [
  {
    label: 'Appointment Reminder',
    message: 'Hi Sarah, this is {AGENT_NAME}. Just a reminder about our appointment tomorrow at 2:00 PM. Please call me if you need to reschedule. Reply STOP to opt out. Msg & data rates may apply.',
  },
  {
    label: 'Call Back Request',
    message: 'Hi Michael, this is {AGENT_NAME}. I wanted to follow up on the life insurance options we discussed. When would be a good time for a quick call? Reply STOP to opt out. Msg & data rates may apply.',
  },
  {
    label: 'Policy Update',
    message: 'Hi Sarah, congratulations! Your life insurance application has been approved. Please give us a call so we can go over the details of your new coverage. Reply STOP to opt out. Msg & data rates may apply.',
  },
  {
    label: 'Policy Review Reminder',
    message: 'Hi Michael, it\'s been a year since we set up your coverage. I wanted to check in and see if we can review your rates this year. Give me a call when you have a moment! Reply STOP to opt out. Msg & data rates may apply.',
  },
  {
    label: 'Client Check-in',
    message: 'Hi Sarah, this is {AGENT_NAME}. Hope all is well! If you ever need anything regarding your coverage, don\'t hesitate to reach out. Reply STOP to opt out. Msg & data rates may apply.',
  },
];

/**
 * Replace {AGENT_NAME} and {BUSINESS_NAME} placeholders in sample message templates
 */
export function personalizeTemplate(template: string, agentName: string, businessName: string): string {
  return template
    .replace(/\{AGENT_NAME\}/g, agentName || 'your agent')
    .replace(/\{BUSINESS_NAME\}/g, businessName || 'your insurance agent');
}

// US States
export const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
] as const;

// A2P Pricing
export const A2P_PRICING = {
  oneTimeFee: 20.00, // $20 one-time fee (covers $4 brand + $15 campaign vetting)
  currency: 'USD',
  twilioBreakdown: {
    brandRegistration: 4.00,
    campaignVetting: 15.00,
  },
  // Monthly campaign fees ($1.50-$10/month) are covered by your subscription
} as const;

// Volume Limits
export const A2P_VOLUME_LIMITS = {
  lowVolumeStandard: {
    dailyLimit: 6000, // 6,000 message segments per day
    description: 'Suitable for most businesses sending under 6,000 messages daily',
  },
} as const;

// Registration Type Details for UI
export const REGISTRATION_TYPE_DETAILS: Record<A2PRegistrationType, {
  title: string;
  subtitle: string;
  benefits: string[];
  limitations?: string[];
  requirements?: string[];
  phoneNumberLimit: number | 'unlimited';
}> = {
  sole_proprietor: {
    title: 'Sole Proprietor',
    subtitle: 'For individual agents just getting started',
    benefits: [
      'No EIN required - uses your SSN',
      'Quick approval process',
      'Perfect for getting started',
    ],
    limitations: [
      'Limited to 1 phone number per campaign',
    ],
    phoneNumberLimit: 1,
  },
  low_volume_standard: {
    title: 'Low Volume Standard',
    subtitle: 'For established businesses with growth plans',
    benefits: [
      'Multiple phone numbers allowed',
      'Higher message throughput',
      'Better for growing agencies',
    ],
    requirements: [
      'EIN required (at least 45 days old)',
    ],
    phoneNumberLimit: 'unlimited',
  },
} as const;
